import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from './api';
import { API_URL } from './api';

const CacheContext = createContext();

const TAGS_CACHE_KEY = 'civote_tags_cache';
const STATS_CACHE_KEY = 'civote_user_stats_cache';
const TAGS_TTL = 5 * 60 * 1000; // 5 minutes
const STATS_TTL = 60 * 1000; // 1 minute

// Helper to get cached data with TTL check
const getCachedData = (key, ttl) => {
  try {
    const cached = localStorage.getItem(key);
    if (cached) {
      const { data, timestamp } = JSON.parse(cached);
      if (Date.now() - timestamp < ttl) {
        return data;
      }
    }
  } catch (e) {
    console.warn('Cache read error:', e);
  }
  return null;
};

// Helper to set cached data
const setCachedData = (key, data) => {
  try {
    localStorage.setItem(key, JSON.stringify({
      data,
      timestamp: Date.now()
    }));
  } catch (e) {
    console.warn('Cache write error:', e);
  }
};

export function CacheProvider({ children }) {
  const [tags, setTags] = useState(() => getCachedData(TAGS_CACHE_KEY, TAGS_TTL) || []);
  const [tagsLoading, setTagsLoading] = useState(!tags.length);
  const [userStats, setUserStats] = useState(() => getCachedData(STATS_CACHE_KEY, STATS_TTL));
  const [statsLoading, setStatsLoading] = useState(!userStats);

  // Load tags with caching
  const loadTags = useCallback(async (forceRefresh = false) => {
    if (!forceRefresh) {
      const cached = getCachedData(TAGS_CACHE_KEY, TAGS_TTL);
      if (cached && cached.length > 0) {
        setTags(cached);
        setTagsLoading(false);
        return cached;
      }
    }

    try {
      setTagsLoading(true);
      const response = await axios.get(`${API_URL}/api/tags`);
      setTags(response.data);
      setCachedData(TAGS_CACHE_KEY, response.data);
      return response.data;
    } catch (error) {
      console.error('Failed to load tags:', error);
      return [];
    } finally {
      setTagsLoading(false);
    }
  }, []); // Empty deps - function doesn't depend on state

  // Load user stats with caching
  const loadUserStats = useCallback(async (forceRefresh = false) => {
    if (!forceRefresh) {
      const cached = getCachedData(STATS_CACHE_KEY, STATS_TTL);
      if (cached) {
        setUserStats(cached);
        setStatsLoading(false);
        return cached;
      }
    }

    try {
      setStatsLoading(true);
      const response = await axios.get(`${API_URL}/api/user/stats`);
      setUserStats(response.data);
      setCachedData(STATS_CACHE_KEY, response.data);
      return response.data;
    } catch (error) {
      console.error('Failed to load user stats:', error);
      return null;
    } finally {
      setStatsLoading(false);
    }
  }, []); // Empty deps - function doesn't depend on state

  // Invalidate stats cache (call after voting)
  const invalidateStatsCache = useCallback(() => {
    localStorage.removeItem(STATS_CACHE_KEY);
    setUserStats(null);
  }, []);

  // Invalidate all caches
  const invalidateAllCaches = useCallback(() => {
    localStorage.removeItem(TAGS_CACHE_KEY);
    localStorage.removeItem(STATS_CACHE_KEY);
    setTags([]);
    setUserStats(null);
  }, []);

  // Load tags on mount
  useEffect(() => {
    loadTags();
  }, [loadTags]);

  const value = {
    // Tags
    tags,
    tagsLoading,
    loadTags,
    
    // User Stats
    userStats,
    statsLoading,
    loadUserStats,
    
    // Cache management
    invalidateStatsCache,
    invalidateAllCaches
  };

  return (
    <CacheContext.Provider value={value}>
      {children}
    </CacheContext.Provider>
  );
}

export function useCache() {
  const context = useContext(CacheContext);
  if (!context) {
    throw new Error('useCache must be used within a CacheProvider');
  }
  return context;
}

export default CacheContext;
